import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Search, Check, ArrowRight, Target, FileSearch, MessageSquare } from 'lucide-react';

const RechercheService = () => {
  return (
    <>
      <Helmet>
        <title>Recherche de Bateau sur Mesure | Service Expert validatedboat</title>
        <meta name="description" content="Service professionnel de recherche de bateau sur mesure. Notre expert identifie, analyse et sélectionne les meilleurs bateaux selon vos critères. Gain de temps et sécurité garantis." />
        <meta name="keywords" content="recherche bateau sur mesure, trouver voilier, expert recherche bateau, sélection bateau, service recherche nautique" />
      </Helmet>

      <div className="min-h-screen bg-[#1a1c1b]">
        <Header />
        
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#302f2c]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Search className="text-[#d9fb06]" size={32} />
                  <span className="text-[#d9fb06] font-bold uppercase text-sm">Service Premium</span>
                </div>
                <h1 className="font-black text-5xl md:text-7xl leading-tight text-[#d9fb06] uppercase mb-6">
                  Recherche de Bateau sur Mesure
                </h1>
                <p className="text-xl text-[#dfddd6] mb-8 leading-relaxed">
                  Gagnez des semaines de recherche. Notre expert identifie, analyse et sélectionne les 5-10 meilleurs bateaux correspondant parfaitement à vos critères.
                </p>
                <div className="flex items-center space-x-4 text-[#888680] mb-8">
                  <div className="flex items-center space-x-2">
                    <Check className="text-[#d9fb06]" size={20} />
                    <span>10-15h de travail expert</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="text-[#d9fb06]" size={20} />
                    <span>Rapport détaillé</span>
                  </div>
                </div>
                <Link to="/#contact">
                  <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-10 py-6 text-lg font-bold uppercase transition-all hover:scale-105">
                    Démarrer ma recherche
                  </Button>
                </Link>
              </div>
              <div className="relative h-[500px] rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1576072869865-d96bb7eaa1d2?w=800"
                  alt="Recherche de bateau"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="absolute w-full h-0.5 bg-[#d9fb06]"></div>
                  <div className="absolute h-full w-0.5 bg-[#d9fb06]"></div>
                  <div className="w-24 h-24 rounded-full border-2 border-[#d9fb06]"></div>
                  <div className="w-32 h-32 rounded-full border border-[#d9fb06]/70"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comment ça marche */}
        <section className="py-24 bg-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <h2 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase text-center mb-16">
              Comment ça marche ?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  title: 'Définition de vos besoins',
                  description: 'Entretien approfondi pour comprendre votre programme de navigation, budget, préférences et contraintes.',
                  icon: <MessageSquare size={40} />
                },
                {
                  step: '02',
                  title: 'Recherche exhaustive',
                  description: 'Scan de toutes les plateformes (Bandofboats, Leboncoin, Youboat, annonces internationales) avec mes critères experts.',
                  icon: <FileSearch size={40} />
                },
                {
                  step: '03',
                  title: 'Sélection et présentation',
                  description: 'Short-list de 5-10 bateaux avec analyse comparative détaillée. Organisation des visites si souhaité.',
                  icon: <Target size={40} />
                }
              ].map((item) => (
                <Card key={item.step} className="bg-[#302f2c] border border-[#3f4816] p-8 relative overflow-hidden group hover:border-[#d9fb06] transition-all">
                  <div className="absolute top-4 right-4 text-[#3f4816] font-black text-6xl opacity-20 group-hover:text-[#d9fb06] transition-colors">
                    {item.step}
                  </div>
                  <div className="text-[#d9fb06] mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[#d9fb06] mb-4 uppercase">
                    {item.title}
                  </h3>
                  <p className="text-[#888680] leading-relaxed">
                    {item.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Ce que vous recevez */}
        <section className="py-24 bg-[#302f2c]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <h2 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase text-center mb-16">
              Ce que vous recevez
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Rapport de recherche complet (20-30 pages)',
                'Sélection de 5-10 bateaux pertinents',
                'Analyse comparative détaillée (prix, équipements, état)',
                'Historique et points forts/faibles de chaque bateau',
                'Estimation budget total (achat + travaux prévisibles)',
                'Recommandations personnalisées',
                'Coordonnées vendeurs et organisation visites',
                'Support conseil pendant 30 jours'
              ].map((item) => (
                <div key={item} className="flex items-start space-x-3 bg-[#1a1c1b] border border-[#3f4816] p-6 rounded-lg">
                  <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={24} />
                  <span className="text-[#dfddd6] text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pourquoi nous */}
        <section className="py-24 bg-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase mb-8">
                  Pourquoi ce service ?
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-[#d9fb06] mb-2">Gain de temps considérable</h3>
                    <p className="text-[#888680] leading-relaxed">
                      Des semaines de recherche condensées en quelques jours. Vous recevez directement les meilleures opportunités.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#d9fb06] mb-2">Accès au marché complet</h3>
                    <p className="text-[#888680] leading-relaxed">
                      Surveillance de toutes les plateformes (FR, UK, IT, ES) + réseau professionnel + annonces non publiques.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#d9fb06] mb-2">Expertise 20 ans</h3>
                    <p className="text-[#888680] leading-relaxed">
                      Analyse technique immédiate. Je repère les bonnes affaires ET les pièges à éviter (vices cachés, surévaluation).
                    </p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#d9fb06] mb-2">Objectivité garantie</h3>
                    <p className="text-[#888680] leading-relaxed">
                      Aucun lien avec les vendeurs. Mon seul intérêt : trouver LE bateau parfait pour vous.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#3f4816] rounded-lg p-8">
                <h3 className="text-3xl font-bold text-[#d9fb06] mb-6 uppercase">Résultats clients</h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-5xl font-black text-[#d9fb06] mb-2">10-15h</p>
                    <p className="text-[#dfddd6]">Temps de travail expert pour vous</p>
                  </div>
                  <div>
                    <p className="text-5xl font-black text-[#d9fb06] mb-2">5-10</p>
                    <p className="text-[#dfddd6]">Bateaux pertinents identifiés</p>
                  </div>
                  <div>
                    <p className="text-5xl font-black text-[#d9fb06] mb-2">100%</p>
                    <p className="text-[#dfddd6]">Clients ayant trouvé leur bateau idéal</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifs */}
        <section className="py-24 bg-[#302f2c]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <h2 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase text-center mb-16">
              Nos Formules
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-[#1a1c1b] border border-[#3f4816] p-8 text-center">
                <h3 className="text-xl font-bold text-[#d9fb06] uppercase mb-4">Essentiel</h3>
                <p className="text-4xl font-black text-[#d9fb06] mb-6">800€</p>
                <ul className="text-[#888680] text-left space-y-3 mb-8">
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Recherche sur mesure</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">5-8 bateaux sélectionnés</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Rapport comparatif</span>
                  </li>
                </ul>
              </Card>

              <Card className="bg-[#3f4816] border-2 border-[#d9fb06] p-8 text-center transform scale-105">
                <div className="bg-[#d9fb06] text-[#1a1c1b] text-xs font-bold uppercase px-3 py-1 rounded-full inline-block mb-4">Populaire</div>
                <h3 className="text-xl font-bold text-[#d9fb06] uppercase mb-4">Premium</h3>
                <p className="text-4xl font-black text-[#d9fb06] mb-6">1 200€</p>
                <ul className="text-[#dfddd6] text-left space-y-3 mb-8">
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Tout Essentiel +</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Organisation visites</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Support 30 jours</span>
                  </li>
                </ul>
              </Card>

              <Card className="bg-[#1a1c1b] border border-[#3f4816] p-8 text-center">
                <h3 className="text-xl font-bold text-[#d9fb06] uppercase mb-4">Complet</h3>
                <p className="text-4xl font-black text-[#d9fb06] mb-6">1 500€</p>
                <ul className="text-[#888680] text-left space-y-3 mb-8">
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Tout Premium +</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Accompagnement signature</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-[#d9fb06] flex-shrink-0 mt-1" size={18} />
                    <span className="text-sm">Support illimité</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 bg-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
            <h2 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase mb-6">
              Prêt à trouver votre bateau idéal ?
            </h2>
            <p className="text-xl text-[#888680] mb-8">
              Réservez votre appel découverte gratuit pour discuter de votre projet
            </p>
            <Link to="/#contact">
              <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-10 py-6 text-lg font-bold uppercase transition-all hover:scale-105 inline-flex items-center space-x-2">
                <span>Réserver mon appel gratuit</span>
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default RechercheService;